import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

// Smooth scroll target — the WhatsApp contact card section.
const TARGET = "#devis";

const MotionWrap = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

/* 1. After Pillars — Full-width bronze banner with dark button */
export const CTABannerGold = () => (
  <section
    data-testid="cta-banner-gold"
    className="relative bg-bronze overflow-hidden"
  >
    {/* subtle decorative texture */}
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.08),transparent_60%)] pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14 sm:py-16 lg:py-20">
      <MotionWrap className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 lg:gap-12 text-center lg:text-left">
        <div className="flex-1">
          <h3 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] font-normal">
            Prêt à transformer
            <span className="italic"> votre espace ?</span>
          </h3>
        </div>

        <div className="flex flex-col items-center lg:items-end gap-3 shrink-0">
          <a
            href={TARGET}
            data-testid="cta-banner-gold-button"
            className="group inline-flex items-center justify-center gap-3 bg-charcoal-deep hover:bg-charcoal text-white px-8 py-5 text-xs uppercase tracking-[0.25em] transition-all duration-300 hover:shadow-xl"
          >
            Demander un devis gratuit
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
          </a>
          <span className="text-white/80 text-[11px] tracking-[0.2em] uppercase">
            Réponse sous 24h · Sans engagement
          </span>
        </div>
      </MotionWrap>
    </div>
  </section>
);

/* 2. After Services — Simple centered line on cream */
export const CTASimpleLine = () => (
  <section
    data-testid="cta-simple-line"
    className="bg-cream py-16 lg:py-20 border-t border-border"
  >
    <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
      <MotionWrap>
        <p className="font-serif text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-snug font-normal">
          Un projet en tête ? <span className="italic text-bronze">On vous répond sous 24h.</span>
        </p>
        <a
          href={TARGET}
          data-testid="cta-simple-button"
          className="group mt-8 inline-flex items-center justify-center gap-3 border border-bronze text-bronze hover:bg-bronze hover:text-white px-8 py-4 text-xs uppercase tracking-[0.25em] transition-all duration-300"
        >
          Obtenir mon devis gratuit
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
        </a>
      </MotionWrap>
    </div>
  </section>
);

/* 3. After Before/After — Centered, filled gold button */
export const CTAFilledGold = () => (
  <section
    data-testid="cta-filled-gold"
    className="bg-cream-alt py-20 lg:py-24"
  >
    <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
      <MotionWrap>
        <p className="font-serif text-charcoal text-3xl sm:text-4xl lg:text-5xl leading-[1.1] font-normal">
          Votre appartement
          <br className="sm:hidden" />
          <span className="italic"> pourrait être</span> le prochain.
        </p>
        <a
          href={TARGET}
          data-testid="cta-filled-gold-button"
          className="group mt-10 inline-flex items-center justify-center gap-3 bg-bronze hover:bg-bronze-hover text-white px-10 py-5 text-xs uppercase tracking-[0.25em] transition-all duration-300 shadow-[0_8px_24px_-6px_rgba(92,92,92,0.4)] hover:shadow-[0_12px_32px_-6px_rgba(92,92,92,0.55)]"
        >
          Je veux le même résultat
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
        </a>
      </MotionWrap>
    </div>
  </section>
);

/* 4. After Stats (WhyUs), before Testimonials — Soft centered */
export const CTASoftCentered = () => (
  <section
    data-testid="cta-soft-centered"
    className="bg-cream py-16 lg:py-20"
  >
    <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
      <MotionWrap>
        <div className="h-px w-12 bg-bronze mx-auto mb-8" />
        <p className="font-serif text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-snug font-normal">
          Rejoignez <span className="italic text-bronze">nos clients satisfaits.</span>
        </p>
        <a
          href={TARGET}
          data-testid="cta-soft-button"
          className="group mt-8 inline-flex items-center justify-center gap-3 bg-charcoal-deep hover:bg-charcoal text-white px-8 py-4 text-xs uppercase tracking-[0.25em] transition-all duration-300"
        >
          Demander mon devis
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
        </a>
      </MotionWrap>
    </div>
  </section>
);
